import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarX, Check, X as XIcon, Hourglass, Warning } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'zojuist';
    if (diff < 3600) return `${Math.floor(diff / 60)} min geleden`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
    return `${Math.floor(diff / 86400)} dag${Math.floor(diff / 86400) > 1 ? 'en' : ''} geleden`;
}

/**
 * Card shown on the instructor's Home tab listing pending cancellations
 * (annulering-aanvragen) so they can accept or reject each one.
 */
export default function PendingCancellationsCard() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(null); // id currently being acted on

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/cancellations`, { withCredentials: true });
            const pending = (data || []).filter(c => (c.status || 'pending') === 'pending');
            setItems(pending);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const act = async (id, action) => {
        if (busy) return;
        setBusy(id);
        try {
            await axios.post(`${API}/cancellations/${id}/${action}`, null, { withCredentials: true });
            toast.success(action === 'accept' ? 'Annulering geaccepteerd' : 'Annulering geweigerd');
            setItems(prev => prev.filter(i => i.id !== id));
        } catch {
            toast.error('Actie mislukt — probeer opnieuw');
        }
        setBusy(null);
    };

    if (items.length === 0) return null;

    return (
        <div className="bg-[#2D3748] border-2 border-amber-400/50 rounded-2xl overflow-hidden" data-testid="pending-cancellations-card">
            <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-400/30 flex items-center gap-2">
                <CalendarX size={20} weight="fill" className="text-amber-400" />
                <h2 className="text-sm font-semibold text-white uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Annuleringsverzoeken
                </h2>
                <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-[#1A202C] text-[11px] font-bold leading-none">
                    {items.length}
                </span>
            </div>
            <div className="divide-y divide-[#4A5568]/60">
                {items.map((c) => (
                    <div key={c.id} className="p-3 sm:p-4" data-testid={`pending-cancellation-${c.id}`}>
                        <div className="flex items-start gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-sm font-semibold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {c.student_name || 'Leerling'}
                                    </span>
                                    {c.within_24h && (
                                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-red-500/15 text-red-300 border border-red-400/40 rounded-full px-1.5 py-0.5">
                                            <Warning size={9} weight="fill" />
                                            &lt;24u
                                        </span>
                                    )}
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-400/40 rounded-full px-1.5 py-0.5">
                                        <Hourglass size={9} weight="fill" />
                                        Pending
                                    </span>
                                </div>
                                <div className="text-xs text-gray-400 mt-0.5">
                                    {c.lesson_date}{c.lesson_time ? ` om ${c.lesson_time}` : ''} ({c.lesson_duration || 60} min)
                                </div>
                                <div className="text-xs text-gray-300 mt-1">
                                    <span className="text-gray-500">Reden: </span>{c.reason_type}{c.reason && c.reason !== c.reason_type ? ` — ${c.reason}` : ''}
                                </div>
                                <div className="text-[10px] text-gray-500 mt-0.5">{timeAgo(c.created_at)}</div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => act(c.id, 'reject')}
                                disabled={busy === c.id}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-red-400/40 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-sm font-semibold py-2 transition-colors disabled:opacity-50"
                                data-testid={`reject-cancellation-${c.id}`}
                            >
                                <XIcon size={16} weight="bold" />
                                Weigeren
                            </button>
                            <button
                                onClick={() => act(c.id, 'accept')}
                                disabled={busy === c.id}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold py-2 transition-colors disabled:opacity-50"
                                data-testid={`accept-cancellation-${c.id}`}
                            >
                                <Check size={16} weight="bold" />
                                Accepteren
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
